import { createSelector, createStructuredSelector } from "reselect"
import createCachedSelector from "re-reselect"
import { Map, List, Set } from "immutable"
import moment from "moment"

import { searchPaths } from "shared/imports/sharedPaths"
import { isSameDay } from "shared/imports/sharedHelperFunctions"
import { getPermissionedSearchItems } from "shared/search/helperFunctions"

import { DAY_STRING, WEEK_STRING, MONTH_STRING, YEAR_STRING } from "shared/imports/sharedConstants"

import {
    selectSearch,
    selectAppearanceValues,
    selectSearchFlvSlice,
    selectAllFilters,
    selectSerializeFunctions,
    selectAllDeserializedFilters,
    selectAllActiveSerializedFilters,
    selectCurrentDeserializedFilters,
} from "shared/search/selectors"

import { convertFilterKeysToFilterIds } from "shared/search/action-creators"

export const selectIsInSearch = createSelector(
    (_, props) => props.location.pathname,
    (_, props) => props.isSearchify,
    (location, isSearchify) => Object.values(searchPaths).some((path) => location.includes(path)) || isSearchify,
)

export const selectAdvancedSearchQdt = createSelector(selectSearch, (search) =>
    search.getIn(["advancedSearch", "currentQDT"]),
)

export const selectSearchHelpIsOpen = (state, props) => selectSearch(state, props).get("isSearchHelpOpen", false)

export const selectNextSearchURL = createSelector([selectSearch], (search) => search.get("nextSearchURL"))

export const selectListViewScrollTop = (state, props) =>
    selectAppearanceValues(state, props).getIn(["scrollTopPositions", "listView"])

export const selectFilterSetScrollTop = (state, props) =>
    selectAppearanceValues(state, props).getIn(["scrollTopPositions", "filterSet"])

export const selectSearchTerm = createSelector([selectSearch], (search) =>
    search.getIn(["advancedSearch", "searchTerm"]),
)

export const selectSearchPlaceholderText = createSelector([selectSearch], (search) =>
    search.getIn(["advancedSearch", "placeholderText"]),
)

export const selectQuorumDataType = createSelector([selectSearch], (search) =>
    search.getIn(["advancedSearch", "currentQDT"]),
)

export const selectCurrentUniqueFlvId = createSelector([selectSearch], (search) =>
    search.getIn(["advancedSearch", "currentUniqueFlvId"]),
)

export const selectIsAiSearchApplied = createSelector([selectSearch], (search) =>
    search.getIn(["aiSearch", "isApplied"]),
)

export const selectAiSearchFilters = createSelector([selectSearch], (search) =>
    convertFilterKeysToFilterIds(
        search,
        search.getIn(["advancedSearch", "currentUniqueFlvId"]),
        search.getIn(["aiSearch", "filterKeys"]),
    ),
)

/* *******************************************************************
 * VISUALIZATION SELECTORS
 ******************************************************************** */

/*
 * Given a set of button groups, remove the buttons that shouldn't
 * be there given the user's region / permission.
 */
export const selectPermissionedButtonGroups = createCachedSelector(
    (_, props) => props.buttonGroups,
    (buttonGroups) =>
        buttonGroups.map((group) =>
            Object.assign(
                {},
                group,
                // filter out any undefined scenes (can be undefined if scene not allowed for user's current region)
                { dropdownScenes: getPermissionedSearchItems(group.dropdownScenes.filter((scene) => scene)) },
            ),
        ),
)((state, props) => props.uniqueFlvId)

/*
 * Given a list of button groups (button groups divide the all the visualization scenes
 * up into certain categories), get a list of all the visualization scenes.
 */
export const selectVisualizationScenes = createCachedSelector(selectPermissionedButtonGroups, (buttons) =>
    buttons.reduce((acc, val) => [...acc, ...val.dropdownScenes], []),
)((state, props) => props.uniqueFlvId)

/*
 * Get whether the visualization console bar should be seen; if there's only one scene,
 * don't show the bar!
 */
export const selectShouldShowVisualizationConsoleBar = createCachedSelector(
    selectVisualizationScenes,
    (scenes) => scenes.length > 1,
)((state, props) => props.uniqueFlvId)

/*
 * Get the active visualization scene from the active id!
 * If there's no activeId, go to the default.
 * If there's no default (either because not specified or because it got
 *    permissioned out), go to the first visualization scene.
 * If there's no visualization scene, the what the hell are you doing?
 */
export const selectActiveVisualizationId = createCachedSelector(
    [
        (state, props) => selectSearchFlvSlice(state, props).getIn(["visualizations", "activeVisualizationId"]),
        (_, props) => props.defaultActiveSceneId,
        selectVisualizationScenes,
    ],
    (activeId, defaultId, scenes) => {
        const hasDefaultScene = scenes.some((scene) => scene.defaultProps.uniqueVizSceneId === defaultId)

        return activeId || (hasDefaultScene && defaultId) || (scenes.length && scenes[0].defaultProps.uniqueVizSceneId)
    },
)((state, props) => props.uniqueFlvId)

/*
 * Get the active visualization from the calculated active visualization ID.
 */
export const selectActiveVisualization = createCachedSelector(
    [selectActiveVisualizationId, selectVisualizationScenes],
    (activeId, scenes) => scenes.find((scene) => scene.defaultProps.uniqueVizSceneId === activeId),
)((state, props) => props.uniqueFlvId)

export const selectVisualizationSlice = (state, props) =>
    selectSearchFlvSlice(state, props).getIn(["visualizations", props.uniqueVizSceneId], Map())

export const selectIsVisualizationRegistered = (state, props) => selectVisualizationSlice(state, props).size > 0

export const selectSecondaryGraphKeys = (state, props) =>
    selectVisualizationSlice(state, props).get("secondaryGraphKeys", Map())

export const selectIsVisualizationLoading = (state, props) =>
    selectVisualizationSlice(state, props).get("isLoading", false)

export const selectVisualizationData = (state, props) => selectVisualizationSlice(state, props).get("data", List())

export const selectExtraVisualizationData = (state, props) =>
    selectVisualizationSlice(state, props).get("extraData", Map())

export const selectVisualizationFilterIds = (state, props) =>
    selectVisualizationSlice(state, props).get("visualizationFilterIds", List())

/*
 * Check if any filters that are associated with the map have value.
 * This is used by the 'Reset Filters' button in the visualization sidebar --
 * the button is disabled if this selector returns false, because if there
 * are no relevant active filters then there are no filters to reset.
 * We use all filters instead of current filters here because sometimes
 * a visualization is attached to a subfilter (from a multifilter), and
 * subfilters always have { hasValue: false }.
 */
export const selectVisualizationFiltersActive = createCachedSelector(
    [selectAllFilters, selectVisualizationFilterIds],
    (allFilters, vizFilterIds) => vizFilterIds.some((filterId) => Boolean(allFilters.getIn([filterId, "value"]))),
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Is the data empty? This selector is a little more complicated because
 * empty datasets are presented differently depending on the visualization type.
 * For maps, a JSON will return, but each state / country will have a count of 0.
 * For other visualizations, an empty dataset will return.
 *
 * Thus, we have to check both cases! In the case of the latter, it's as
 * simple as checking whether the dataset is empty -- but only after we've
 * finished loading!
 */
export const selectIsVisualizationDataEmpty = createCachedSelector(
    [selectVisualizationData, selectIsVisualizationLoading],
    (data, isLoading) => !data.size && !isLoading,
    // (data.size > 0 && data.every(item => item.get('value') === 0)) ||
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Returns the selected values in the graph. Used for bar and pie graphs,
 * where you can click on a bar / slice.
 */
export const selectVisualizationSelectedValues = createCachedSelector(selectVisualizationSlice, (slice) =>
    slice.get("selectedValues", List()).toJS(),
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Select all the graphs that have been stored as layers!
 * This will select the entirety of the stored graph data.
 */
export const selectStoredGraphs = createCachedSelector(selectVisualizationSlice, (slice) =>
    slice.get("storedGraphs", Map()),
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Select the data of stored graphs, but only the stored graphs
 * that are currently visible to the user. Used in toggling
 * layers off and on.
 */
export const selectStoredGraphData = createCachedSelector(selectStoredGraphs, (slice) =>
    slice
        .filter((value) => value.get("isDisplayed"))
        .reduce((acc, value, key) => acc.set(key, value.get("data")), Map()),
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Select the titles of stored graphs and whether they're active
 * (for the layer table)
 */
export const selectStoredGraphTableData = createCachedSelector(selectStoredGraphs, (slice) =>
    slice.reduce((acc, value, key) => [...acc, { label: key, active: value.get("isDisplayed"), id: key }], []),
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

// Must get the first available graph order number here.
// Most commonly, it will be size + 1. However, in cases of people
// adding and then deleted saved graphs, it could be less than size.
// (For instance, I have two saved graphs: [a, b]). a has order 1, b has order 2.
// I then remove a. b still has order 2 -- b's order will remain unchanged
// as long as nobody deletes it, so it will always have the same order
// and hence the same color. Then I add c. Instead of adding c in the order 3 space,
// I need to put it in the order 1 space, so we don't run out of colors.
export const selectNextStoredGraphOrderNumber = createCachedSelector(selectStoredGraphs, (storedGraphs) => {
    // there only potential order numbers we should be looking at
    // are less than the array's size. For instance, if I had nine graphs
    // and deleted all of them except for the one with order 9, then
    // the next graph should only look at numbers [1, 2] for potential
    // next order.

    // 0 will always be the current graph, so stored graph orders
    // start at 1.
    const potentialOrderNumbers = [...Array(storedGraphs.size + 1).keys()].map((i) => i + 1)
    const existingOrderNumbers = storedGraphs.reduce((acc, val) => [...acc, val.get("order")], [])
    return potentialOrderNumbers.filter((num) => !existingOrderNumbers.includes(num)).sort()[0]
})((state, props) => `${props.uniqueFlvId}${props.visualizationType}`)

/*
 * Select the serialization functions for the visualization's associated
 * filters. This is necessary to convert our interactions with the visualizations,
 * which are often in array form, into a serialized version for the store.
 */
export const selectVisualizationSerializeFunctions = createCachedSelector(
    [selectAllFilters, selectSerializeFunctions, selectVisualizationFilterIds],
    (filters, serializeFunctions, vizFilterIds) => {
        if (!vizFilterIds) {
            return
        }

        return vizFilterIds.reduce((acc, uniqueFilterId) => {
            const serializationKey = filters.getIn([uniqueFilterId, "serializationKey"])
            const serializeFunc = serializeFunctions.get(serializationKey)

            return serializeFunc ? Object.assign({}, acc, { [uniqueFilterId]: serializeFunc }) : acc
        }, {})
    },
)((state, props) => `${props.uniqueFlvId}${props.visualizationType}`)

/*
 * Select the values of the filters associated with the visualization.
 * Note: this relies on having "getFilterValues" in props!!!!
 */
export const selectVisualizationFilterValues = createCachedSelector(
    [selectAllDeserializedFilters, (state, props) => props.getFilterValues],
    (filters, getFilterValues) => {
        if (!getFilterValues) {
            return
        }

        const valuesOnly = filters.map((slice) => slice.get("value"))
        return getFilterValues(valuesOnly)
    },
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Select the filterKeys of the filters associated with the visualization.
 * Note: this relies on having "getFilterKeys" in props!!!!
 */
export const selectVisualizationFilterKeys = createCachedSelector(
    [selectAllDeserializedFilters, (state, props) => props.getFilterKeys],
    (filters, getFilterKeys) => {
        if (!getFilterKeys) {
            return
        }

        const filterKeysOnly = filters.map((slice) => slice.get("filterKey"))
        return getFilterKeys(filterKeysOnly)
    },
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * given visualization data already in the store, get the sidebar table data.
 * We truncate at the table at 100 elements. Most data should already come sorted,
 * but map data doesn't, hence the required sorting.
 */
export const selectVisualizationTableData = createCachedSelector(
    [selectVisualizationData, selectVisualizationSelectedValues],
    (data, selectedValues) =>
        data
            .sort((a, b) => b.get("value") - a.get("value"))
            .slice(0, 100)
            .map((datum) => datum.set("active", selectedValues.includes(datum.get("id"))))
            .toJS(),
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Get the selected organization design id for the graph.
 */
export const selectVisualizationOrgDesignId = createCachedSelector(selectExtraVisualizationData, (extraData) =>
    extraData.getIn(["design", "orgDesignId"]),
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Get the desired graph colors, from organization design.
 */
export const selectVisualizationDesignColorConfigs = createCachedSelector(selectExtraVisualizationData, (extraData) => {
    const configs = extraData.getIn(["design", "colorConfigs"])
    if (configs) {
        return configs.toJS()
    }
})((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Very specific case: check if we want to show only current members
 */
export const selectCurrentOnlyStatus = createCachedSelector(selectExtraVisualizationData, (extraData) =>
    extraData.get("currentOnly"),
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/* *******************************************************************
 * GRAPH SPECIFIC SELECTORS
 ******************************************************************** */

/* *******************************************************************
 * MAP
 ******************************************************************** */
/*
 * Get the map geography data that has loaded. In order to not
 * constantly reload the same shape file, we'll put the in the store under
 * its URL.
 */
export const selectMapGeographyData = createCachedSelector(
    [selectSearch, (_, props) => props.configs.geoDataUrl],
    (search, url) => search.getIn(["mapGeographyData", url], List()).toJS(),
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Given a map's filter, get which regions are currently selected in the
 * filter. Because different filter types have different value formats,
 * we need to do some processing.
 */
export const selectMapSelectedRegions = createCachedSelector(
    (state, props) => props.visualizationFilterValues,
    (filterValues) => {
        if (Array.isArray(filterValues)) {
            return filterValues
        } else if (filterValues && filterValues.addedIds) {
            return filterValues.addedIds
        }
        return []
    },
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

export const selectMapProps = createStructuredSelector({
    selectedRegions: selectMapSelectedRegions,
    formattedGeographyData: selectMapGeographyData,
})

/* *******************************************************************
 * WORD CLOUD
 ******************************************************************** */
/*
 * Select the visible terms in the word cloud. We truncate the word cloud
 * list at 250 -- the word cloud definitely doesn't show 250 words anyway,
 * and I don't know if anyone cares about the 300th most commonly used word
 * in a set of documents.
 *
 * Because users can remove certain words from the word cloud,
 * we must keep a record of these removed words and make sure they don't
 * show up.
 */
export const selectShownTerms = createCachedSelector(
    [selectVisualizationData, (state, props) => selectExtraVisualizationData(state, props).get("removedTerms", List())],
    (data, removedTerms) =>
        data
            .filter((wordObj) => !removedTerms.includes(wordObj.get("id")))
            .sortBy(
                (wordObj) => wordObj.get("value"),
                (a, b) => b - a,
            )
            .slice(0, 250),
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

export const selectHiddenTerms = (state, props) =>
    selectExtraVisualizationData(state, props).get("hiddenTerms", List()) || List()

export const selectRemovedTerms = (state, props) =>
    selectExtraVisualizationData(state, props).get("removedTerms", List()) || List()

export const wordCloudProps = createStructuredSelector({
    shownTerms: selectShownTerms,
    removedTerms: selectRemovedTerms,
    hiddenTerms: selectHiddenTerms,
})

/* *******************************************************************
 * BAR / PIE
 ******************************************************************** */
export const selectCurrentBarPieGraph = createCachedSelector(
    selectExtraVisualizationData,
    (extraData) => extraData.get("currentGraph") || "bar", // the default graph is the bar graph
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

export const selectCurrentOnlyData = createCachedSelector(
    [selectCurrentOnlyStatus, selectVisualizationData],
    (currentOnly, vizData) => {
        return currentOnly ? vizData.filter((datum) => datum.get("current")).slice(0, 100) : vizData.slice(0, 100)
    },
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

// produces the same data as `selectCurrentOnlyData`, except with
// an "active" field set on any data that has currently been selected
// by the user.
export const selectCurrentOnlyTableData = createCachedSelector(
    [selectCurrentOnlyData, selectVisualizationSelectedValues],
    (data, selectedValues) => {
        return data.map((datum) => datum.set("active", selectedValues.includes(datum.get("id")))).toJS()
    },
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

export const barPieProps = createStructuredSelector({
    currentGraph: selectCurrentBarPieGraph,
    currentOnlyData: selectCurrentOnlyData,
    currentOnlyTableData: selectCurrentOnlyTableData,
})

/*******************************************************************
 * LINE GRAPH
 *
 * unfortunately we have all sorts of date formats flying about
 * this is an attempt to standardize them all to JS Date objects
 * (not ideal, but that's what d3 uses)
 ******************************************************************* */
/*
 * Get the value of the date filters associated with the line graph!
 */
export const selectFilterDates = createCachedSelector(
    (state, props) => props.visualizationFilterValues,
    (filterValues) => {
        return {
            start: filterValues.start && moment(filterValues.start, "MMMM Do, YYYY").toDate(),
            end: filterValues.end && moment(filterValues.end, "MMMM Do, YYYY").toDate(),
        }
    },
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * gets the extent of the line x-axis, including when there are
 * multiple lines. Multiple lines don't necessarily span the entire date range --
 * we can have one line that only has data from 2014 and another line that only
 * has data from 2018.
 */
export const selectDataMaxDateRange = createCachedSelector(
    [selectVisualizationData, selectStoredGraphData],
    (currentData, storedData) => {
        const earliestDate = storedData.reduce(
            (acc, data) => (data.first().get("date") < acc ? data.first().get("date") : acc),
            currentData.first() && currentData.first().get("date"),
        )

        const latestDate = storedData.reduce(
            (acc, data) => (data.last().get("date") > acc ? data.last().get("date") : acc),
            currentData.last() && currentData.last().get("date"),
        )

        return {
            start: earliestDate,
            end: latestDate,
        }
    },
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * get the current date range of the graph. If there are date filters applied,
 * they take precedence. Otherwise, we're bounded only by how much data
 * we have in the current and stored lines.
 */
export const selectCurrentGraphDateRange = createCachedSelector(
    [selectFilterDates, selectDataMaxDateRange],
    (filterDates, maxDates) => {
        const start = filterDates.start || maxDates.start
        const end = filterDates.end || maxDates.end

        return {
            start,
            end,
        }
    },
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Get the data of our current line only within the graph date range.
 * The visualization doesn't need to know about any data it's not supposed
 * to show.
 */
export const selectFilteredLineDateData = createCachedSelector(
    [selectVisualizationData, selectCurrentGraphDateRange],
    (data, boundingDates) =>
        data.filter((datum) => datum.get("date") >= boundingDates.start && datum.get("date") <= boundingDates.end),
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Get the data of our stored line only within the graph date range.
 * The visualization doesn't need to know about any data it's not supposed
 * to show.
 */
export const selectStoredGraphFilteredDateData = createCachedSelector(
    [selectStoredGraphData, selectCurrentGraphDateRange],
    (dataCollection, boundingDates) =>
        dataCollection.map((data) =>
            data.filter((datum) => datum.get("date") >= boundingDates.start && datum.get("date") <= boundingDates.end),
        ),
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Give the graph the data it wants in the format it wants:
 * [ { key, data, order }, { key, data, order}, ... ]
 */
export const selectFormattedStoredGraphFilteredDateData = createCachedSelector(
    [selectStoredGraphFilteredDateData, selectStoredGraphs],
    (filteredData, storedGraphs) => {
        return filteredData.reduce((acc, value, key) => {
            const graphObj = {
                key,
                data: value.toJS(),
                order: storedGraphs.getIn([key, "order"]),
            }

            return [...acc, graphObj]
        }, [])
    },
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Given the lines that are bounded by the graph's date range, find
 * the maximum count over the date range and over all lines!
 */
export const selectCurrentGraphMaxCount = createCachedSelector(
    [selectFilteredLineDateData, selectStoredGraphFilteredDateData],
    (currentData, storedData) => {
        const currentDataMax = currentData.map((value) => value.get("count")).max()
        const dataMax = storedData.reduce((acc, data) => {
            const max = data.map((value) => value.get("count")).max()
            return max > acc ? max : acc
        }, currentDataMax || 0)

        // for some padding at the top, the +1 is in case dataMax is 0
        return dataMax * 1.1 + 1
    },
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

const getDateString = (date, interval) => {
    switch (interval) {
        case DAY_STRING:
            return date.toLocaleString("en-us", { month: "long", day: "numeric", year: "numeric" })
        case WEEK_STRING:
            return date.toLocaleString("en-us", { month: "long", day: "numeric", year: "numeric" })
        case MONTH_STRING:
            return date.toLocaleString("en-us", { month: "long", year: "numeric" })
        case YEAR_STRING:
            return date.getFullYear()
        default:
            return date.toLocaleString("en-us", { month: "long", year: "numeric" })
    }
}

/*
 * For each date, get some statistics that the visualization might use.
 * For instance:
 *      Out of all the lines, what is the maximum count at date X?
 *      What are the values of all the lines at date X?
 *      What is the value of the current line at date X?
 *
 * These are pieces of data that the visualization could technically do,
 * but that would make the d3 messy and we'd have to recompute it all the time.
 * Hence, a selector!
 */
export const selectLineDerivedData = createCachedSelector(
    [selectFilteredLineDateData, selectStoredGraphFilteredDateData, selectSecondaryGraphKeys],
    (currentData, storedData, secondaryGraphKeys) => {
        const currentDataDates = currentData.map((datum) => datum.get("date"))
        const storedDates = storedData.reduce((acc, data) => acc.concat(data.map((datum) => datum.get("date"))), List())

        // cast to set to get rid of duplicates, sort it by date, and then cast
        // to array. What a life.
        const allDates = Set(currentDataDates.concat(storedDates)).sort().toList()

        const intervalKey = secondaryGraphKeys.get("interval") || MONTH_STRING

        // gets the maximum count at each date, and also returns the
        // value of every applicable line at the date.
        return allDates
            .map((date) => {
                const currentLineCount = currentData
                    .find((datum) => isSameDay(datum.get("date"), date), undefined, Map())
                    .get("count")

                const storedLineDetails = storedData.reduce((acc, data, key) => {
                    const datum = data.find((datum) => isSameDay(datum.get("date"), date))
                    return datum ? Object.assign({}, acc, { [key]: datum.get("count") }) : acc
                }, {})

                const maxCount = Object.values(storedLineDetails).reduce(
                    (acc, value) => (value > acc ? value : acc),
                    currentLineCount || 0,
                )

                const dateString = getDateString(date, intervalKey)

                return {
                    date,
                    dateString,
                    maxCount,
                    currentLineCount,
                    storedLineDetails,
                }
            })
            .toJS()
    },
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Check if any stored graph is the exact facsimile of the current graph.
 * This happens most often immediately after a line is saved.
 * We need to check this because we don't want to show "Current Search"
 * in the legend unless absolutely necessary.
 */
export const selectShouldShowCurrentSearchInLegend = createCachedSelector(
    [selectFilteredLineDateData, selectStoredGraphFilteredDateData],
    (currentData, storedData) => !Boolean(storedData.find((value) => value.equals(currentData))),
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*
 * Checks if we have more data than we are currently showing. If so, we can zoom back out!
 */
export const selectShouldShowUndoZoom = createCachedSelector(
    [selectFilterDates, selectDataMaxDateRange],
    (filterDates, maximumDateRange) => {
        return maximumDateRange.start < filterDates.start || maximumDateRange.end > filterDates.end
    },
)((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

export const lineSidebarProps = createStructuredSelector({
    shouldShowUndoZoom: selectShouldShowUndoZoom,
    maximumDateRange: selectDataMaxDateRange,
})

export const lineWrapperProps = createStructuredSelector({
    dateData: selectFilteredLineDateData,
    storedGraphData: selectFormattedStoredGraphFilteredDateData,
    graphDateRange: selectCurrentGraphDateRange,
    graphCountMax: selectCurrentGraphMaxCount,
    graphDerivedData: selectLineDerivedData,
    showCurrentSearchInLegend: selectShouldShowCurrentSearchInLegend,
})

/*******************************************************************
 * CALENDAR
 *
 * All the dates are formatted as moments here.
 ******************************************************************* */
/*
 * Gets the range of the calendar dates. This will be the bound of a month.
 */
export const selectCalendarDateRange = createCachedSelector(selectExtraVisualizationData, (extraData) => ({
    start: extraData.getIn(["calendarRange", "start"]) || moment().startOf("month").startOf("week"),
    end: extraData.getIn(["calendarRange", "end"]) || moment().endOf("month").endOf("week"),
}))((state, props) => `${props.uniqueFlvId}${props.uniqueVizSceneId}`)

/*******************************************************************
 * NETWORK GRAPH
 ******************************************************************* */

export const selectNetworkGraphProps = createStructuredSelector({
    currentFilters: selectAllActiveSerializedFilters,
})

export const selectCurrentDeserializedFiltersStore = createSelector(
    [(state) => state, (state, props) => props, selectQuorumDataType, selectCurrentUniqueFlvId],
    (state, props, quorumDataType, uniqueFlvId) =>
        selectCurrentDeserializedFilters(state, {
            ...props,
            quorumDataType,
            uniqueFlvId,
        }),
)

// selects an arbitrary value from the
// search => filterableListViews => ${uniqueFlvId}
// slice of the store
export const selectSearchValue = (key) =>
    createSelector(selectSearchFlvSlice, (searchFLV) =>
        Array.isArray(key) ? searchFLV && searchFLV.getIn(key) : searchFLV && searchFLV.get(key),
    )
